import { startLoading, setUserOrganization } from "./userOrganizationSlice";
import axios from "axios";
import { zynity_api } from "../../config/external-routes";
import { constants } from "../../config/constants";

const isLocalEnv = process.env.REACT_APP_ENV === constants.envs.local;

/** Empty payload so Body renders "No organizations" and isLoading becomes false */
const emptyOrgsPayload = { data: [] };

/**
 * Live DB column is `valuecreated` (all lower, no underscore); JSON may expose that key
 * literally. Other stacks use `ValueCreated` / `value_created`. UI expects `valueCreated`.
 */
function pickOrgValueCreated(raw: Record<string, unknown>): string | null | undefined {
    if (raw.valueCreated != null) return raw.valueCreated as string | null | undefined;
    if (raw.ValueCreated != null) return raw.ValueCreated as string | null | undefined;
    if (raw.value_created != null) return raw.value_created as string | null | undefined;
    if (raw.valuecreated != null) return raw.valuecreated as string | null | undefined;
    return undefined;
}

function normalizeTeamRecord(team: any) {
    if (!team || typeof team !== "object") return team;
    const next = { ...team };
    if (next.ValueCreated == null && next.valueCreated != null) {
        next.ValueCreated = next.valueCreated;
    }
    if (next.ValueCreated == null && next.value_created != null) {
        next.ValueCreated = next.value_created;
    }
    if (next.ValueCreated == null && next.valuecreated != null) {
        next.ValueCreated = next.valuecreated;
    }
    return next;
}

function normalizeOrganizationRecord(org: any) {
    if (!org || typeof org !== "object") return org;
    const next = { ...org };
    const vc = pickOrgValueCreated(next);
    if (vc !== undefined) {
        next.valueCreated = vc;
    }
    if (Array.isArray(next.teams)) {
        next.teams = next.teams.map(normalizeTeamRecord);
    }
    return next;
}

function normalizeOrganizationsPayload(payload: any) {
    if (!payload || typeof payload !== "object") return payload;

    const mapOrgs = (list: any[]) => list.map(normalizeOrganizationRecord);

    if (Array.isArray(payload.data)) {
        return { ...payload, data: mapOrgs(payload.data) };
    }

    if (
        payload.data &&
        typeof payload.data === "object" &&
        Array.isArray((payload.data as { data?: unknown }).data)
    ) {
        const inner = payload.data as { data: any[]; [k: string]: unknown };
        return {
            ...payload,
            data: {
                ...inner,
                data: mapOrgs(inner.data),
            },
        };
    }

    return payload;
}

export const getUserOrganizations = () => {
    return async (dispatch: (arg0: { payload: any; type: string }) => void, state: any) => {
        const user = state().auth;

        if (isLocalEnv && (user?.id === 0 || user?.id == null)) {
            dispatch(setUserOrganization(emptyOrgsPayload));
            return;
        }

        dispatch(startLoading());
        axios.defaults.headers.common = {
            Authorization: `Bearer ${user.access_token}`,
        };

        const authHeader = { headers: { Authorization: `Bearer ${user.access_token}` } };
        const lightUrl = zynity_api.get_user_organization_light.replace("{user_id}", user.id);
        const fullUrl = zynity_api.get_user_organization.replace("{user_id}", user.id);

        try {
            const [lightRes, fullRes] = await Promise.all([
                axios.get(lightUrl, authHeader),
                axios.get(fullUrl, authHeader),
            ]);
            const raw = fullRes.data ?? lightRes.data;
            dispatch(setUserOrganization(normalizeOrganizationsPayload(raw)));
        } catch {
            dispatch(setUserOrganization(emptyOrgsPayload));
        }
    };
};
